import { useState, useEffect, useRef } from "react";
import { AddToHomeScreen, DeviceType, DeviceInfo, AddToHomeScreenOptions, BeforeInstallPromptEvent } from "@/lib/add-to-homescreen";

interface AddToHomeScreenPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onDontShowAgain: () => void;
  options: AddToHomeScreenOptions;
}

export function AddToHomeScreenPrompt({ 
  isOpen, 
  onClose, 
  onDontShowAgain, 
  options 
}: AddToHomeScreenPromptProps) {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [cssLoaded, setCssLoaded] = useState(false);
  const [localeLoaded, setLocaleLoaded] = useState(false);
  const [localeCatalog, setLocaleCatalog] = useState<{ [key: string]: any }>({});
  const addToHomeScreenRef = useRef<ReturnType<typeof AddToHomeScreen> | null>(null);

  useEffect(() => {
    if (isOpen && !cssLoaded) {
      // Lazy-load CSS only when needed for performance
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/libs/add-to-homescreen/styles.css';
      link.onload = () => setCssLoaded(true);
      link.onerror = () => {
        console.error('Failed to load add-to-homescreen CSS');
        setCssLoaded(false);
      };
      document.head.appendChild(link);
    }
  }, [isOpen, cssLoaded]);

  useEffect(() => {
    if (!addToHomeScreenRef.current) {
      // Initialize the library instance with proper configuration
      addToHomeScreenRef.current = AddToHomeScreen(options);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for when the app is actually installed
    const handleAppInstalled = () => {
      console.log('App was installed');
      localStorage.setItem('snugglecrafters_app_installed', 'true');
      onClose();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [options, onClose]);

  // Load locale files when component opens
  useEffect(() => {
    if (isOpen && !localeLoaded) {
      const loadLocales = async () => {
        try {
          const locale = navigator.language.split('-')[0] || 'en';
          const supportedLocales = ['en', 'es', 'fr', 'de', 'it', 'ja', 'pt', 'ru', 'zh'];
          const targetLocale = supportedLocales.includes(locale) ? locale : 'en';
          
          const response = await fetch(`/libs/add-to-homescreen/locales/${targetLocale}.json`);
          if (response.ok) {
            const catalog = await response.json();
            setLocaleCatalog(catalog);
          } else {
            // Fallback to English
            const enResponse = await fetch('/libs/add-to-homescreen/locales/en.json');
            if (enResponse.ok) {
              const enCatalog = await enResponse.json();
              setLocaleCatalog(enCatalog);
            }
          }
          setLocaleLoaded(true);
        } catch (error) {
          console.error('Failed to load locale files:', error);
          setLocaleLoaded(true); // Continue with fallbacks
        }
      };
      
      loadLocales();
    }
  }, [isOpen, localeLoaded]);

  useEffect(() => {
    if (isOpen && addToHomeScreenRef.current && cssLoaded && localeLoaded) {
      // Don't auto-trigger the library's show method as it can auto-display modal
      // Instead, just use the library for device detection
      const locale = navigator.language.split('-')[0] || 'en';
      try {
        // Use library's device detection without auto-showing
        const lib = addToHomeScreenRef.current;
        const deviceInfo = new DeviceInfo(
          lib.isStandAlone(),
          !lib.isStandAlone() && (lib.isDeviceIOS() || lib.isDeviceAndroid() || 
            (lib.isDesktopChrome() && !!window.navigator.serviceWorker) ||
            (lib.isDesktopEdge() && !!window.navigator.serviceWorker)),
          lib.isDeviceIOS() ? DeviceType.IOS :
            lib.isDeviceAndroid() ? DeviceType.ANDROID : DeviceType.DESKTOP
        );
        setDeviceInfo(deviceInfo);
      } catch (error) {
        console.error('Error detecting device info:', error);
      }
    }
  }, [isOpen, cssLoaded, localeLoaded]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt using proper typing
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        localStorage.setItem('snugglecrafters_app_installed', 'true');
      }
      setDeferredPrompt(null);
    }
    onClose();
  };

  const handleModalClose = () => {
    if (addToHomeScreenRef.current) {
      addToHomeScreenRef.current.closeModal();
    }
    onClose();
  };

  const getBrowserSpecificClasses = (): string => {
    if (!addToHomeScreenRef.current || !deviceInfo) return '';
    
    const lib = addToHomeScreenRef.current;
    let classes = 'adhs-container visible';
    
    if (deviceInfo.device === DeviceType.IOS) {
      classes += ' adhs-ios adhs-mobile';
      
      if (lib.isBrowserIOSSafari()) {
        classes += ' adhs-safari';
        // Check for iPad specifically
        if (navigator.userAgent.includes('iPad') || 
           (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)) {
          classes += ' adhs-ipad';
        }
      } else if (lib.isBrowserIOSChrome()) {
        classes += ' adhs-chrome';
      } else if (lib.isBrowserIOSFirefox()) {
        classes += ' adhs-firefox';
      }
    } else if (deviceInfo.device === DeviceType.ANDROID) {
      classes += ' adhs-android adhs-mobile';
      
      if (lib.isBrowserAndroidChrome()) {
        classes += ' adhs-chrome';
      } else if (lib.isBrowserAndroidSamsung()) {
        classes += ' adhs-samsung';  
      } else if (lib.isBrowserAndroidFirefox()) {
        classes += ' adhs-firefox';
      }
    } else {
      classes += ' adhs-desktop';
      
      if (lib.isDesktopChrome()) {
        classes += ' adhs-desktop-chrome';
      } else if (lib.isDesktopEdge()) {
        classes += ' adhs-desktop-edge';
      } else if (lib.isDesktopSafari()) {
        classes += ' adhs-desktop-safari';
      }
    }
    
    return classes;
  };

  const getLocalizedText = (key: string, fallback: string): string => {
    // Use the loaded locale catalog first, then fallback
    if (localeCatalog[key]) {
      return localeCatalog[key];
    }
    
    // Fallback to English defaults
    const fallbacks: { [key: string]: string } = {
      'Add to Home Screen': 'Add to Home Screen',
      'Add To Dock': 'Add To Dock',
      'An icon will be added to your home screen so you can quickly access this website.': 
        'An icon will be added to your home screen so you can quickly access this website.',
      'An icon will be added to your Taskbar so you can quickly access this website.': 
        'An icon will be added to your Taskbar so you can quickly access this website.',
      'An icon will be added to your Dock so you can quickly access this website.': 
        'An icon will be added to your Dock so you can quickly access this website.',
      'Install': 'Install',
      'Later': 'Later'
    };
    
    return fallbacks[key] || fallback;
  };

  const getInstructions = () => {
    if (!deviceInfo || !addToHomeScreenRef.current) return null;

    const lib = addToHomeScreenRef.current;

    if (deviceInfo.device === DeviceType.IOS) {
      // iPad Safari has special handling
      const isIPad = navigator.userAgent.includes('iPad') || 
                    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
      
      if (lib.isBrowserIOSSafari()) {
        return {
          title: getLocalizedText('Add to Home Screen', 'Add to Home Screen'),
          steps: [
            getLocalizedText('Tap the Share button', 'Tap the Share button'),
            getLocalizedText('Scroll down and tap \'Add to Home Screen\'', 'Scroll down and tap \'Add to Home Screen\''),
            getLocalizedText('Tap \'Add\' to place the app icon on your home screen', 'Tap \'Add\' to place the app icon on your home screen')
          ],
          images: ["ios-safari-sharing-api-button.svg", "ios-safari-add-to-home-screen-button.svg"],
          isIPad
        };
      } else if (lib.isBrowserIOSChrome()) {
        return {
          title: getLocalizedText('Add to Home Screen', 'Add to Home Screen'),
          steps: [
            getLocalizedText('Tap the menu button', 'Tap the menu button'),
            getLocalizedText('Select \'Add to Home screen\'', 'Select \'Add to Home screen\'')
          ],
          images: ["ios-chrome-more-button.svg", "ios-chrome-add-to-home-screen-button.svg"],
          isIPad
        };
      }
    } else if (deviceInfo.device === DeviceType.ANDROID) {
      return {
        title: getLocalizedText('Install', 'Install'),
        steps: [
          getLocalizedText('Tap the menu button (⋮) in your browser', 'Tap the menu button (⋮) in your browser'),
          getLocalizedText('Select \'Add to Home screen\' or \'Install app\'', 'Select \'Add to Home screen\' or \'Install app\''),
          getLocalizedText('Confirm by tapping \'Add\' or \'Install\'', 'Confirm by tapping \'Add\' or \'Install\'')
        ],
        images: ["android-chrome-more-button.svg", "android-chrome-add-to-home-screen-button.svg"]
      };
    } else {
      // Desktop
      const titleText = lib.isDesktopMac() ? 
        getLocalizedText('Add To Dock', 'Add To Dock') : 
        getLocalizedText('Install', 'Install');
      
      return {
        title: titleText,
        steps: [
          getLocalizedText('Look for the install button in your browser\'s address bar', 'Look for the install button in your browser\'s address bar'),
          getLocalizedText('Click it to install as a desktop app', 'Click it to install as a desktop app'),
          getLocalizedText('Or go to browser menu and select \'Install\'', 'Or go to browser menu and select \'Install\'')
        ],
        images: ["generic-more-button.svg"]
      };
    }

    return null;
  };

  const getBlurbText = (): string => {
    if (!deviceInfo || !addToHomeScreenRef.current) return '';
    
    const lib = addToHomeScreenRef.current;
    
    if (deviceInfo.device === DeviceType.DESKTOP) {
      if (lib.isDesktopMac()) {
        return getLocalizedText(
          'An icon will be added to your Dock so you can quickly access this website.',
          'An icon will be added to your Dock so you can quickly access this website.'
        );
      } else {
        return getLocalizedText(
          'An icon will be added to your Taskbar so you can quickly access this website.',
          'An icon will be added to your Taskbar so you can quickly access this website.'
        );
      }
    }
    
    return getLocalizedText(
      'An icon will be added to your home screen so you can quickly access this website.',
      'An icon will be added to your home screen so you can quickly access this website.'
    );
  };

  const getCorrectArrowContainer = () => {
    if (!deviceInfo || !addToHomeScreenRef.current || !options.showArrow) return null;

    const lib = addToHomeScreenRef.current;

    if (deviceInfo.device === DeviceType.IOS) {
      // iPad gets special arrow container
      const isIPad = navigator.userAgent.includes('iPad') || 
                    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
      
      if (isIPad) {
        return (
          <div className="adhs-ios-ipad-safari-bouncing-arrow-container">
            <img src={`${options.assetUrl}/img/ios-safari-bouncing-arrow.svg`} alt="" />
          </div>
        );
      } else {
        return (
          <div className="adhs-ios-safari-bouncing-arrow-container">
            <img src={`${options.assetUrl}/img/ios-safari-bouncing-arrow.svg`} alt="" />
          </div>
        );
      }
    } else if (deviceInfo.device === DeviceType.ANDROID) {
      return (
        <div className="adhs-android-chrome-bouncing-arrow-container">
          <img src={`${options.assetUrl}/img/android-chrome-bouncing-arrow.svg`} alt="" />
        </div>
      );
    } else if (deviceInfo.device === DeviceType.DESKTOP && lib.isDesktopSafari()) {
      return (
        <div className="adhs-desktop-safari-bouncing-arrow-container">
          <img src={`${options.assetUrl}/img/desktop-safari-bouncing-arrow.svg`} alt="" />
        </div>
      );
    }

    return null;
  };

  if (!isOpen || !cssLoaded || !localeLoaded || !deviceInfo || deviceInfo.isStandAlone) {
    return null;
  }

  // Don't show if device can't be standalone
  if (!deviceInfo.canBeStandAlone) {
    return null;
  }

  const instructions = getInstructions();
  if (!instructions) return null;

  const containerClass = getBrowserSpecificClasses();

  return (
    <div className={containerClass} onClick={handleModalClose}>
      <div className="adhs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="adhs-logo">
          <img src={options.appIconUrl} alt={options.appName} />
        </div>
        
        <div className="adhs-app-name">{options.appName}</div>
        <div className="adhs-app-url">{window.location.hostname}</div>
        
        <div className="adhs-blurb">
          {getBlurbText()}
        </div>

        <div className="adhs-list">
          {instructions.steps.map((step, index) => (
            <div key={index} className="adhs-list-item">
              <div className="adhs-number-container">
                <div className="adhs-circle">
                  <div className="adhs-number">{index + 1}</div>
                </div>
              </div>
              <div className="adhs-instruction">
                {step}
                {instructions.images[index] && (
                  <img 
                    src={`${options.assetUrl}/img/${instructions.images[index]}`}
                    alt=""
                    className="adhs-list-button"
                    style={{ height: '30px', marginLeft: '5px' }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="adhs-button-container">
          {deviceInfo.device === DeviceType.DESKTOP && deferredPrompt && (
            <button onClick={handleInstallClick} className="adhs-button" data-testid="button-install">
              {getLocalizedText('Install', 'Install')}
            </button>
          )}
          <button 
            onClick={onDontShowAgain} 
            className="adhs-button" 
            style={{ backgroundColor: '#666' }}
            data-testid="button-dont-show-again"
          >
            Don't Show Again
          </button>
          <button 
            onClick={handleModalClose} 
            className="adhs-button" 
            style={{ backgroundColor: '#999' }}
            data-testid="button-later"
          >
            {getLocalizedText('Later', 'Later')}
          </button>
        </div>
      </div>

      {/* Render the correct arrow container based on device and browser */}
      {getCorrectArrowContainer()}
    </div>
  );
}